<script>
import { GET_SERVICE_SCHEDULES_BY_VEHICLE } from '@/services/api/maintenance/serviceSchedules'
import { GET_VEHICLE_GROUPS } from '@/services/api/setting/fleet/fleetGroup'

export default {
  apollo: {
    serviceSchedulesByVehicle: {
      client: 'clientLowPrio',
      query: GET_SERVICE_SCHEDULES_BY_VEHICLE,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            vehicleServiceStatus: this.title?.status ?? undefined,
            pagination: {
              page: this.currentPage,
              show: this.perPage,
            },
          },
        }
      },
      result({ data, loading }) {
        if (loading) return

        if (data && this.title?.status) {
          const vehicleIds = data.serviceSchedulesByVehicle.serviceSchedulesByVehicle.data.map(
            (item) => {
              return item.vehicle.id
            }
          )

          this.serviceSchedules = data.serviceSchedulesByVehicle.serviceSchedulesByVehicle
          this.getVGroup(vehicleIds)

          this.isLoading = false
          this.isEmpty = false
        }
      },
    },
  },

  data() {
    return {
      serviceStatuses: [],
      serviceSchedules: [],
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      isEmpty: true,
      isLoading: true,
      title: null
    }
  },

  computed: {
    /**
     * Total no. of records
     */
    totalData() {
      return this.serviceSchedules?.pagination?.total
    },

    childFields() {
      let filteredData = this.fields.filter((obj) => {
        return obj.hasOwnProperty('children')
      })

      return filteredData
    },

    fields() {
      return [
        {
          key: 'licensePlate',
          label: this.$t('global.object.licensePlate'),
          sortable: true,
          headerRowspan: 2,
        },
        {
          key: 'vehicleGroup',
          label: this.$t('global.object.fleetGroup'),
          sortable: true,
          headerRowspan: 2,
          style: 'max-width: 300px',
        },
        {
          key: 'totalServiceSchedule',
          label: this.$t('maintenance.field.totalServiceScheduleComponent'),
          headerColspan: 2,
          sortable: true,
          isRowHeader: true,
          style: 'text-align: center; vertical-align: middle; max-width: 360px;',
          children: [
            {
              key: 'vehiclesAsInProgress',
              label: this.$t('maintenance.status.inService'),
              icon: 'outline_truck-maintenance',
              headerColspan: 1,
              style: 'width:180px',
            },
            {
              key: 'vehiclesAsLate',
              label: this.$t('maintenance.status.lateService'),
              icon: 'outline_alert-triangle',
              headerColspan: 1,
              style: 'width:180px',
            },
            {
              key: 'vehiclesAsSoon',
              label: this.$t('maintenance.status.approachingService'),
              icon: 'outline_timer',
              headerColspan: 1,
              style: 'width:180px',
            },
            {
              key: 'vehiclesAsNormal',
              label: this.$t('maintenance.status.normal'),
              icon: 'outline_checkmark-circle-2',
              headerColspan: 1,
              style: 'width:180px',
            },
          ],
        },
      ]
    },
  },

  methods: {
    async loadServiceScheduleVehicles(title, vgroupId) {
      this.title = title
      this.isLoading = true
      await this.$apollo.queries.serviceSchedulesByVehicle.refetch({
        filter: {
          vehicleServiceStatus: title.status,
          pagination: {
            page: this.currentPage,
            show: this.perPage,
          },
          vgroupId,
        },
      })
    },
    onHide() {
      this.serviceStatuses = []
      this.serviceSchedules = []
      this.currentPage = 1
      this.perPage = 10
      this.pageOptions = [10, 25, 50, 100]
      this.isEmpty = true
      this.isLoading = true
      this.$emit('close')
    },
    handleClickRow(value) {
      this.$router.push({
        name: 'Detail Service Schedule',
        params: {
          vehicleId: value,
        },
      })
    },
    async getVGroup(vehicleId) {
      await this.$apollo
        .query({
          query: GET_VEHICLE_GROUPS,
          variables: {
            filter: {
              vehicleId: vehicleId,
            },
          },
        })
        .then((res) => {
          const vgroups = res.data.vgroups.vgroups

          this.serviceSchedules.data.forEach((item) => {
            let fleetGroup = []
            vgroups.map((group) => {
              const groupFilter = group.vehicleList.includes(
                group.vehicleList.find((el) => el.id === item.vehicle.id)
              )
              if (groupFilter) fleetGroup.push(group.name)
            })

            item.fleetGroup = fleetGroup
          })

          this.serviceStatuses = this.serviceSchedules.data
        })

      return this.serviceStatuses
    },
  },
}
</script>

<template>
  <div v-if="title">
    <b-modal
      modal-class="mc-modal-dashboard"
      id="modal-service-status"
      ref="modal-service-status"
      :title="title.label"
      header-bg-variant="primary"
      header-close-variant="light"
      :ok-title="$t('global.action.close')"
      ok-variant="outline-primary"
      ok-only
      size="lg"
      centered
      scrollable
      @hidden="onHide"
      :per-page="perPage"
      :current-page="currentPage"
    >
      <div class="table-responsive my-3 border border-1" v-if="fields.length > 0">
        <b-skeleton-wrapper :loading="isLoading">
          <template #loading>
            <b-card>
              <b-skeleton-table
                :rows="3"
                :columns="fields.length"
                :table-props="{ bordered: false, striped: true }"
              >
              </b-skeleton-table>
            </b-card>
          </template>
          <div v-if="!isLoading" class="datatables table-responsive-sm">
            <table class="table b-table table-sm" aria-describedby="Service Status Modal Table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    id="header"
                    :key="field.key"
                    :colspan="field.headerColspan"
                    :rowspan="field.headerRowspan"
                    :style="field.style"
                  >
                    {{ field.label }}
                  </th>
                </tr>
                <tr>
                  <template v-if="title.key === 'vehiclesAsInProgress'">
                    <th
                      id="sub-header"
                      v-for="field in childFields[0].children.slice(0, 2)"
                      :key="field.key"
                      :colspan="field.headerColspan"
                      :style="field.style"
                    >
                      {{ field.label }}
                    </th>
                  </template>
                  <template v-if="title.key === 'vehiclesAsLate'">
                    <th
                      id="sub-header"
                      v-for="field in childFields[0].children.slice(1, 3)"
                      :key="field.key"
                      :colspan="field.headerColspan"
                      :style="field.style"
                    >
                      {{ field.label }}
                    </th>
                  </template>
                  <template v-if="title.key === 'vehiclesAsSoon'">
                    <th
                      id="sub-header"
                      v-for="field in childFields[0].children.slice(2, 4)"
                      :key="field.key"
                      :colspan="field.headerColspan"
                      :style="field.style"
                    >
                      {{ field.label }}
                    </th>
                  </template>
                </tr>
              </thead>

              <tbody v-if="!isEmpty">
                <tr v-for="(status, index) in serviceStatuses" :key="index">
                  <td
                    @click="handleClickRow(status.vehicleId)"
                    class="fw-bold text-primary td__noellipsis clickable-field"
                  >
                    {{ status?.vehicle?.licensePlate }}
                  </td>
                  <td>{{ status?.fleetGroup?.toString() }}</td>
                  <td v-if="title.key === 'vehiclesAsInProgress'">{{ status?.totalInProgress }}</td>
                  <td v-if="title.key === 'vehiclesAsInProgress' || title.key === 'vehiclesAsLate'">
                    {{ status?.totalLate }}
                  </td>
                  <td v-if="title.key === 'vehiclesAsLate' || title.key === 'vehiclesAsSoon'">
                    {{ status?.totalSoon }}
                  </td>
                  <td v-if="title.key === 'vehiclesAsSoon' || title.key === 'vehiclesAsNormal'">
                    {{ status?.totalNormal }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-skeleton-wrapper>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <div id="tickets-table_length" class="dataTables_length">
            <label class="d-inline-flex align-items-center">
              {{ $t('global.text.display') }}
              <b-form-select
                class="form-select form-select-sm"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
              ></b-form-select>
              {{ $t('global.text.entries') }}.
              {{ totalData > 0 ? (currentPage - 1) * perPage + 1 : 0 }} -
              {{ Math.min(totalData, currentPage * perPage) }} {{ $t('global.object.of') }}
              {{ totalData }}
            </label>
          </div>
        </div>
        <div class="col-sm-12 col-md-6">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <!-- pagination -->
              <b-pagination
                v-model="currentPage"
                :total-rows="totalData"
                :per-page="perPage"
              ></b-pagination>
            </ul>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>
